import { currencies } from '@/enums';
import { FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { getCurrencyConversion } from '@/redux/modules/currency';
import { preferredCurrencySelector } from '@/redux/modules/bidder/preferences/bidderPreferences.selectors';
import { useAppSelector } from '@/redux/hooks';
import styled from 'styled-components';

type Props = {
    /** For passing in custom styles */
    className?: any;
    /** when true, does not render any cent digits */
    noFraction?: boolean;
    /** The denomination of the source currency e.g. 'USD' or 'GBP' */
    sourceCurrency?: keyof typeof currencies;
    /** The source currency amount in dollars and cents, this value will be converted to the user's preferred currency */
    value: number;
};

const FormattedCurrencyOrPreferred = ({ className, noFraction, sourceCurrency = 'USD', value, ...rest }: Props) => {
    const sourceCurrencyConversion = useAppSelector((state) => getCurrencyConversion(state, sourceCurrency));
    const preferredCurrency = useAppSelector(preferredCurrencySelector);
    const targetCurrency = useAppSelector((state) =>
        getCurrencyConversion(state, preferredCurrency === currencies.NoCurrency ? 'USD' : preferredCurrency)
    );

    if (
        sourceCurrency !== targetCurrency?.currencyCode &&
        (!Boolean(targetCurrency) || !Boolean(sourceCurrencyConversion))
    ) {
        return null;
    }

    const willConvertCurrency = sourceCurrency !== targetCurrency?.currencyCode;

    // Convert source currency to USD, we do this because we only store conversions from usd, so we always need this.
    // Convert from USD to target currency
    const valueInTargetCurrency = willConvertCurrency
        ? (value / sourceCurrencyConversion?.conversionToUsd) * targetCurrency?.conversionToUsd
        : value;

    const fractionDigits = noFraction ? 0 : targetCurrency?.fractionSize;

    return (
        <StyledFormattedCurrency
            className={className}
            {...rest}
        >
            <FormattedNumber
                currency={willConvertCurrency ? targetCurrency?.currencyCode : sourceCurrency}
                key={`formatted-currency-or-preferred-${value}`}
                maximumFractionDigits={fractionDigits}
                minimumFractionDigits={fractionDigits}
                style="currency"
                value={valueInTargetCurrency}
            />
        </StyledFormattedCurrency>
    );
};

const StyledFormattedCurrency = styled.span`
    display: inline-flex;
    align-items: center;
`;

export default FormattedCurrencyOrPreferred;
